<script setup lang="ts">
import DataTable from 'primevue/datatable'
import { computed, onMounted, ref } from 'vue'
import TableSearchInput from '@/components/table/TableSearchInput.vue'
import TableFilter from '@/components/table/TableFilter.vue'
import Button from 'primevue/button'
import { Icon } from '@iconify/vue/dist/iconify.js'
import Column from 'primevue/column'
import { formatedDate } from '@/constants/index'
import {getYarnById} from '@/api/yarns'
import type {Usage} from '@/types/yarn-types'
import type {Batch} from "@/types/batch-types";
import {getBatchList} from "@/api/batches";
import router from "@/router";
import type {Product} from "@/types/product-types";
import {getProductList} from "@/api/products";

const searchQuery = ref('')
const loading = ref(true)
const batches = ref<Batch[] | undefined>()
const yarnId = router.currentRoute.value.params.id as string
const yarn = ref<any>()
const usages = ref<Usage[]>([])
const qualities = ref<Product[]>([])

const filteredUsages = computed(() => {
  const query = searchQuery.value.toLowerCase()
  return usages.value.filter(
    (usage: Usage) =>
      usage.batches?.toLowerCase().includes(query) ||
      String(usage.percentageUsed)?.includes(query)
  )
})

const getBatchNumber = (batchId: string | undefined) => {
  return batches.value?.find((batch) => batch.id == batchId)?.batchNumber
}

const getQualityName = (qualityId: string | undefined) => {
  if (!qualityId) return 'N/A'
  const quality = qualities.value.find((quality: Product) => quality.id === qualityId)
  return quality?.unfinishedReference || 'N/A'
}

onMounted(async () => {
  batches.value = await getBatchList()
  qualities.value = await getProductList()
  yarn.value = await getYarnById(yarnId);
  usages.value = yarn.value?.mergeUsages || []
  loading.value = false
})
</script>

<template>
  <DataTable
    :value="filteredUsages"
    paginator
    :rows="10"
    tableStyle="min-width: 50rem"
    stripedRows
    paginatorTemplate="PrevPageLink CurrentPageReport NextPageLink"
    :rowsPerPageOptions="[10, 25]"
    currentPageReportTemplate="Showing {first} to {last} of {totalRecords} suppliers"
    :loading="loading"
    :pt="{
      table: loading && 'min-h-[30rem]'
    }"
    :pt-options="{
      mergeProps: true
    }"
    style="width: 100%;"
  >
    <template #empty> <p class="text-center">No Usage found.</p> </template>
    <template #header>
      <div class="flex items-center justify-between font-normal">
        <TableSearchInput v-model:searchQuery="searchQuery" />
        <TableFilter />
      </div>
    </template>

    <Column field="kiloOfYarnUsed" header="Kilo of Yarn Used" sortable style="width: 20%;">
      <template #body="slotProps">
        <p class="px-4">{{ slotProps.data.kiloOfYarnUsed }} Kg</p>
      </template>
    </Column>

    <Column field="dateUsed" header="Date Knitted" sortable style="width: 20%;">
      <template #body="slotProps">
        <p class="px-4">
          {{ formatedDate(slotProps.data.dateKnitted) }}
        </p>
      </template>
    </Column>

    <Column field="percentageUsed" header="Percentage Used" sortable style="width: 20%;">
      <template #body="slotProps">
        <p class="px-4">{{ parseFloat(slotProps.data.percentageUsed).toFixed(2) }}%</p>
      </template>
    </Column>

    <Column field="batches" header="Batches" sortable style="width: 20%;">
      <template #body="slotProps">
        <RouterLink
          :to="`/batches/${slotProps.data.batchId}`"
          class="px-4 font-medium text-neutral-800 block hover:underline"
          >{{ getBatchNumber(slotProps.data.batchId) }}
        </RouterLink>
      </template>
    </Column>

    <Column field="knittingQualityId" header="Quality" sortable style="width: 16%;">
      <template #body="slotProps">
        <RouterLink
            v-if="slotProps.data.knittingQualityId"
            :to="`/productSpecification/${slotProps.data.knittingQualityId}`"
            class="px-4 font-medium text-neutral-800 block hover:underline"
        >
          {{ getQualityName(slotProps.data.knittingQualityId) }}
        </RouterLink>
        <p v-else class="px-4 text-gray-500">
          {{ getQualityName(slotProps.data.knittingQualityId) }}
        </p>
      </template>
    </Column>

    <!------------------------------------------ pagination---------------------------------------------------->
    <template #paginatorprevpagelinkicon>
      <Button size="small" class="space-x-1 items-center" outlined severity="secondary">
        <Icon icon="material-symbols:chevron-left" class="text-2xl" />
        <span>Previous</span>
      </Button>
    </template>
    <template #paginatornextpagelinkicon>
      <Button size="small" outlined severity="secondary" class="space-x-1 items-center">
        <span>Next</span>
        <Icon icon="material-symbols:chevron-right" class="text-2xl" />
      </Button>
    </template>
  </DataTable>
</template>